import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Sparkles } from 'lucide-react';
import { getErrorMessage } from '../utils/errorHandler';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing (React StrictMode)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Extract session_id from URL hash
      const hash = location.hash;
      console.log('AuthCallback: Raw hash:', hash);
      
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');
      console.log('AuthCallback: Extracted session_id:', sessionId);

      if (!sessionId) {
        toast.error('Authentication failed - no session ID');
        navigate('/login');
        return;
      }

      try {
        console.log('AuthCallback: Calling processGoogleCallback...');
        const userData = await processGoogleCallback(sessionId);
        console.log('AuthCallback: Success, userData:', userData);
        
        // Clear the URL hash
        window.history.replaceState(null, '', window.location.pathname);
        
        if (userData.needs_onboarding) {
          navigate('/onboarding', { state: { user: userData } });
        } else {
          toast.success(`Welcome back, ${userData.name}!`);
          navigate('/dashboard', { state: { user: userData } });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        console.error('Error response:', error.response?.data);
        const errorMessage = getErrorMessage(error, 'Authentication failed');
        toast.error(`Sign in failed: ${errorMessage}`);
        navigate('/login');
      }
    };

    processAuth();
  }, [location.hash, navigate, processGoogleCallback]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <Sparkles className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-pulse" />
        <Loader2 className="w-8 h-8 text-amber-500 mx-auto animate-spin mb-4" />
        <p className="text-white text-lg">Completing sign in...</p>
        <p className="text-slate-400 text-sm mt-2">Please wait while we set up your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
